import { Component, EventEmitter, Input, Output, signal, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CameraConfigDto, CameraDto, CameraType, ConfidenceReference, CreateCameraRequest, DeviceAddressDto } from '../../../../../api/gen';
import { PointDirection } from '../../../../../api/gen';
import { DEFAULT_TIMEZONE_ID, getTimezoneOptions } from './timezone.constants';

@Component({
    selector: 'app-create-camera-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatCheckboxModule,
        MatProgressBarModule,
        MatTooltipModule
    ],
    templateUrl: './create-camera-form.component.html',
    styleUrl: './create-camera-form.component.scss'
})
export class CreateCameraFormComponent implements OnChanges {

    @Input() isProcessing = false;
    @Input() camera: CameraDto | null = null;
    @Input() mode: 'create' | 'edit' = 'create';
    @Output() createCamera = new EventEmitter<any>();
    @Output() cancel = new EventEmitter<void>();

    cameraForm!: FormGroup;
    showAdvanced = signal(false);
    hidePassword = signal(true);
    copiedField = signal<string | null>(null);

    cameraTypeOptions = Object.values(CameraType);
    pointDirectionOptions = Object.values(PointDirection);
    confidenceReferenceOptions = Object.values(ConfidenceReference);
    timeZoneOptions = getTimezoneOptions();

    constructor(private fb: FormBuilder, private snackBar: MatSnackBar) {
        this.initForm();
    }

    copyText(text: string | undefined | null, label: string): void {
        if (!text) return;
        navigator.clipboard.writeText(text);
        this.copiedField.set(label);
        this.snackBar.open(`${label} copied to clipboard`, 'Close', { duration: 2500 });
        setTimeout(() => {
            if (this.copiedField() === label) {
                this.copiedField.set(null);
            }
        }, 2000);
    }

    onHttpsChange(isHttps: boolean): void {
        const portCtrl = this.cameraForm.get('config.port');
        if (portCtrl) {
            const currentPort = portCtrl.value;
            if (isHttps && currentPort === 80) {
                portCtrl.setValue(443);
            } else if (!isHttps && currentPort === 443) {
                portCtrl.setValue(80);
            }
        }
    }

    ConfidenceReferenceShowValue(confidenceReference: any): string {
        switch (confidenceReference) {
            case 'Scale0To100':
                return '0 - 100%';
            case 'Scale0To1':
                return '0.0 - 1.0';
            case 'Scale0To255':
                return '0 - 255';
            default:
                return confidenceReference;
        }
    }

    getDirectionLabel(dir: PointDirection | string): string {
        switch (dir) {
            case PointDirection.Approach:
                return 'Approach (Vehicle Arrival)';
            case PointDirection.Departure:
                return 'Departure (Vehicle Exit)';
            case PointDirection.Both:
                return 'Both (Bidirectional)';
            case PointDirection.Unknown:
            default:
                return 'Unknown / Unmapped';
        }
    }
    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['camera'] || changes['mode']) && this.cameraForm) {
            if (this.camera) {
                this.patchFormFromCamera(this.camera);
            } else {
                this.cameraForm.reset({
                    deviceName: '',
                    deviceDescription: '',
                    deviceHardwareID: '',
                    algorithmVersion: '',
                    buildDate: new Date().toISOString().slice(0, 10),
                    cameraInfo: '',
                    systemVersion: '',
                    deviceAddress: {
                        firstAddressLine: '',
                        secondAddressLine: '',
                        locality: '',
                        postCode: '',
                        region: '',
                        countryCode: '',
                        latitude: null,
                        longitude: null
                    },
                    config: {
                        ipAddress: '',
                        port: 80,
                        username: '',
                        password: '',
                        useHttps: false,
                        timeZoneId: DEFAULT_TIMEZONE_ID,
                        type: CameraType.Dahua,
                        model: '',
                        confidenceReference: ConfidenceReference.Scale0To100,
                        leftDirectionMapping: PointDirection.Unknown,
                        rightDirectionMapping: PointDirection.Unknown,
                        upDirectionMapping: PointDirection.Unknown,
                        downDirectionMapping: PointDirection.Unknown,
                        logOnly: false
                    }
                });
            }
            this.updatePasswordValidators();
        }
    }

    private updatePasswordValidators(): void {
        const passwordControl = this.cameraForm.get('config.password');
        if (!passwordControl) {
            return;
        }
        if (this.mode === 'create') {
            passwordControl.setValidators([Validators.required]);
        } else {
            passwordControl.clearValidators();
        }
        passwordControl.updateValueAndValidity();
    }

    private initForm(): void {
        this.cameraForm = this.fb.group({
            deviceName: ['', [Validators.required, Validators.minLength(2)]],
            deviceDescription: [''],
            deviceHardwareID: [''],
            algorithmVersion: [''],
            buildDate: [new Date().toISOString()],
            cameraInfo: [''],
            systemVersion: [''],
            deviceAddress: this.fb.group({
                firstAddressLine: [''],
                secondAddressLine: [''],
                locality: [''],
                postCode: [''],
                region: [''],
                countryCode: [''],
                latitude: [null],
                longitude: [null]
            }),
            config: this.fb.group({
                ipAddress: ['', [
                    Validators.required,
                    Validators.pattern(/^((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.){3}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/)
                ]],
                port: [80, [Validators.required, Validators.min(1), Validators.max(65535)]],
                username: ['', Validators.required],
                password: ['', Validators.required],
                useHttps: [false],
                timeZoneId: [DEFAULT_TIMEZONE_ID],
                type: [CameraType.Dahua, Validators.required],
                model: ['', Validators.required],
                confidenceReference: [ConfidenceReference.Scale0To100],
                leftDirectionMapping: [PointDirection.Unknown],
                rightDirectionMapping: [PointDirection.Unknown],
                upDirectionMapping: [PointDirection.Unknown],
                downDirectionMapping: [PointDirection.Unknown],
                logOnly: [false]
            })
        });
    }

    onSubmit(): void {
        if (this.cameraForm.valid) {
            const formData = this.cameraForm.getRawValue();
            if (!formData.deviceHardwareID) {
                const guid = this.generateGuidValue();
                this.cameraForm.patchValue({ deviceHardwareID: guid });
                formData.deviceHardwareID = guid;
            }
            if (formData.buildDate) {
                formData.buildDate = new Date(formData.buildDate).toISOString();
            }
            if (this.mode === 'edit' && !formData.config.password) {
                delete formData.config.password;
            }
            this.createCamera.emit(formData);
        }
    }

    onCancel(): void {
        this.cameraForm.reset({ 'config.useHttps': false, 'config.type': CameraType.Dahua, 'config.port': 80 });
        this.cancel.emit();
    }

    toggleAdvanced(): void {
        this.showAdvanced.update(v => !v);
    }

    generateGUID(): void {
        const guid = this.generateGuidValue();
        this.cameraForm.patchValue({ deviceHardwareID: guid });
    }

    private generateGuidValue(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    private patchFormFromCamera(camera: CameraDto): void {
        const buildDate = camera.buildDate ? new Date(camera.buildDate).toISOString().slice(0, 10) : '';
        this.cameraForm.patchValue({
            deviceName: camera.deviceName || '',
            deviceDescription: camera.deviceDescription || '',
            deviceHardwareID: camera.deviceHardwareID || '',
            algorithmVersion: camera.algorithmVersion || '',
            buildDate: buildDate,
            cameraInfo: camera.cameraInfo || '',
            systemVersion: camera.systemVersion || '',
            deviceAddress: {
                firstAddressLine: camera.deviceAddress?.firstAddressLine || '',
                secondAddressLine: camera.deviceAddress?.secondAddressLine || '',
                locality: camera.deviceAddress?.locality || '',
                postCode: camera.deviceAddress?.postCode || '',
                region: camera.deviceAddress?.region || '',
                countryCode: camera.deviceAddress?.countryCode || '',
                latitude: camera.deviceAddress?.latitude ?? null,
                longitude: camera.deviceAddress?.longitude ?? null
            },
            config: {
                ipAddress: camera.config?.ipAddress || '',
                port: camera.config?.port ?? 80,
                username: camera.config?.username || '',
                password: camera.config?.password || '',
                useHttps: camera.config?.useHttps ?? false,
                timeZoneId: camera.config?.timeZoneId ?? DEFAULT_TIMEZONE_ID,
                type: camera.config?.type ?? CameraType.Dahua,
                model: camera.config?.model || '',
                confidenceReference: camera.config?.confidenceReference ?? ConfidenceReference.Scale0To100,
                leftDirectionMapping: camera.config?.leftDirectionMapping ?? PointDirection.Unknown,
                rightDirectionMapping: camera.config?.rightDirectionMapping ?? PointDirection.Unknown,
                upDirectionMapping: camera.config?.upDirectionMapping ?? PointDirection.Unknown,
                downDirectionMapping: camera.config?.downDirectionMapping ?? PointDirection.Unknown,
                logOnly: camera.config?.logOnly ?? false
            }
        });
    }

    getConfigError(fieldName: string): string {
        const control = this.cameraForm.get(`config.${fieldName}`);
        if (control?.hasError('required')) {
            return `${fieldName} is required`;
        }
        if (control?.hasError('pattern')) {
            return `${fieldName} is invalid`;
        }
        if (control?.hasError('min') || control?.hasError('max')) {
            return `${fieldName} is out of range`;
        }
        return '';
    }
}
